/**
 * INC - the incident record.
 *
 * Severity, source and lifecycle are each a closed list, and the lifecycle is
 * a transition TABLE rather than a comment: `canTransitionIncident` is the one
 * function the API refuses with and the UI draws its buttons from, so the two
 * cannot disagree about what is allowed.
 *
 * Forward skipping is allowed ("not every incident must pass through every
 * state", design doc section 7): NEW -> INVESTIGATING -> MONITORING -> RESOLVED
 * is a normal path, and a false alarm may go NEW -> RESOLVED. Going backwards
 * is allowed to exactly one place, INVESTIGATING, because that is what a
 * regression or a reopen actually is. CLOSED is final.
 */

export const INCIDENT_SEVERITIES = ['SEV1', 'SEV2', 'SEV3', 'SEV4'] as const;
export type IncidentSeverity = (typeof INCIDENT_SEVERITIES)[number];

export const INCIDENT_SEVERITY_META: Readonly<Record<IncidentSeverity, { title: string; response: string; meaning: string }>> = {
  SEV1: { title: 'Critical', response: 'Immediate', meaning: 'Major business interruption: live bidding unavailable, auction integrity in doubt, platform-wide outage, incorrect settlement, customer data at risk.' },
  SEV2: { title: 'Major', response: 'Within minutes', meaning: 'Significant degradation with real customer impact: bid latency, checkout or SMS failing for some tenants, a storefront down.' },
  SEV3: { title: 'Minor', response: 'Same day', meaning: 'Limited impact: one administrative feature broken, analytics or media delayed, an isolated tenant configuration failure.' },
  SEV4: { title: 'Informational', response: 'Tracked', meaning: 'Worth a record, little immediate impact: a transient dependency problem, a non-critical job failure, a threshold warning.' },
};

export const INCIDENT_STATUSES = [
  'NEW', 'ACKNOWLEDGED', 'INVESTIGATING', 'IDENTIFIED', 'MITIGATING', 'MONITORING', 'RESOLVED', 'CLOSED',
] as const;
export type IncidentStatus = (typeof INCIDENT_STATUSES)[number];

export const INCIDENT_SOURCES = [
  'AUTOMATED_MONITOR', 'TENANT_REPORT', 'OPERATOR', 'CUSTOMER_SUPPORT', 'AWS', 'STRIPE', 'TWILIO',
  'GITHUB_DEPLOYMENT', 'SECURITY', 'SYNTHETIC_CHECK', 'AGENT', 'MCP_DIAGNOSTIC',
] as const;
export type IncidentSource = (typeof INCIDENT_SOURCES)[number];

/** Sources that mean HipNumber noticed before a person told us (design doc section 8). */
export const INCIDENT_SOURCES_AUTOMATED: readonly IncidentSource[] = [
  'AUTOMATED_MONITOR', 'AWS', 'STRIPE', 'TWILIO', 'GITHUB_DEPLOYMENT', 'SYNTHETIC_CHECK', 'AGENT', 'MCP_DIAGNOSTIC',
];

export const INCIDENT_TRANSITIONS: Readonly<Record<IncidentStatus, readonly IncidentStatus[]>> = {
  NEW: ['ACKNOWLEDGED', 'INVESTIGATING', 'RESOLVED'],
  ACKNOWLEDGED: ['INVESTIGATING', 'IDENTIFIED', 'MITIGATING', 'MONITORING', 'RESOLVED'],
  INVESTIGATING: ['IDENTIFIED', 'MITIGATING', 'MONITORING', 'RESOLVED'],
  IDENTIFIED: ['MITIGATING', 'MONITORING', 'RESOLVED', 'INVESTIGATING'],
  MITIGATING: ['MONITORING', 'RESOLVED', 'INVESTIGATING'],
  MONITORING: ['RESOLVED', 'INVESTIGATING'],
  RESOLVED: ['CLOSED', 'INVESTIGATING'],
  CLOSED: [],
};

export function canTransitionIncident(from: IncidentStatus, to: IncidentStatus): boolean {
  return INCIDENT_TRANSITIONS[from].includes(to);
}

/** Active means "somebody should be doing something": anything before RESOLVED. */
export const INCIDENT_ACTIVE_STATUSES: readonly IncidentStatus[] = INCIDENT_STATUSES.filter((s) => s !== 'RESOLVED' && s !== 'CLOSED');

export function isIncidentActive(status: IncidentStatus): boolean {
  return INCIDENT_ACTIVE_STATUSES.includes(status);
}

/**
 * Which timestamp a status entry stamps. Detection and creation are stamped
 * at insert; the rest are stamped when the status is first entered and never
 * cleared on a reopen, so MTTA / MTTI / MTTR (design doc section 78) read the
 * FIRST time each milestone was reached.
 */
export const INCIDENT_STATUS_TIMESTAMP: Readonly<Partial<Record<IncidentStatus, 'acknowledgedAt' | 'identifiedAt' | 'mitigatedAt' | 'resolvedAt' | 'closedAt'>>> = {
  ACKNOWLEDGED: 'acknowledgedAt',
  IDENTIFIED: 'identifiedAt',
  MITIGATING: 'mitigatedAt',
  RESOLVED: 'resolvedAt',
  CLOSED: 'closedAt',
};

/** Order for "worst first" sorting: SEV1 is 0. */
export const severityRank = (s: IncidentSeverity): number => INCIDENT_SEVERITIES.indexOf(s);
